import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Divider, Stack, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import client from '../api/client';

function ContentItem({ item, lessonId }) {
  const mediaRef = useRef(null);
  const storageKey = `media-pos-${lessonId}-${item.name || item.type}`;

  useEffect(() => {
    if (!mediaRef.current) return;
    const saved = parseFloat(localStorage.getItem(storageKey));
    if (saved && !isNaN(saved)) {
      mediaRef.current.currentTime = saved;
    }
    const el = mediaRef.current;
    const save = () => localStorage.setItem(storageKey, el.currentTime);
    el.addEventListener('timeupdate', save);
    return () => el.removeEventListener('timeupdate', save);
  }, [storageKey]);

  if (item.type === 'TEXT') {
    return (
      <Box sx={{ mb: 2 }}
           dangerouslySetInnerHTML={{ __html: item.body || '' }} />
    );
  }
  if (item.type === 'VIDEO') {
    return (
      <Box sx={{ mb: 2 }}>
        {item.name && <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{item.name}</Typography>}
        <video ref={mediaRef} controls style={{ width: '100%', maxWidth: 720 }} src={item.url} />
      </Box>
    );
  }
  if (item.type === 'AUDIO') {
    return (
      <Box sx={{ mb: 2 }}>
        {item.name && <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{item.name}</Typography>}
        <audio ref={mediaRef} controls style={{ width: '100%' }} src={item.url} />
      </Box>
    );
  }
  if (item.type === 'DOCUMENT') {
    return (
      <Box sx={{ mb: 2 }}>
        <a href={item.url} target="_blank" rel="noreferrer">{item.name || 'Download'}</a>
      </Box>
    );
  }
  return null;
}

export default function LessonPage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLesson(null);
    setError(null);
    Promise.all([
      client.get(`/courses/${courseId}/lessons/${lessonId}`),
      client.get(`/courses/${courseId}`)
    ])
      .then(([lr, cr]) => {
        setLesson(lr.data);
        setCourse(cr.data);
      })
      .catch(err => {
        if (err.response?.status === 403) setError('access_denied');
        else setError('load_failed');
      });
  }, [courseId, lessonId]);

  function lessonIndex() {
    if (!course) return -1;
    return (course.lessons || []).findIndex(l => l.lessonId === lessonId);
  }

  function prevLesson() {
    const idx = lessonIndex();
    if (idx > 0) return course.lessons[idx - 1].lessonId;
    return null;
  }

  function nextLesson() {
    const idx = lessonIndex();
    if (idx >= 0 && idx < (course.lessons || []).length - 1) return course.lessons[idx + 1].lessonId;
    return null;
  }

  async function handleMarkDone() {
    setMarking(true);
    try {
      await client.post(`/courses/${courseId}/lessons/${lessonId}/progress`);
      const next = nextLesson();
      if (next) {
        navigate(`/courses/${courseId}/lessons/${next}`);
      } else {
        navigate(`/courses/${courseId}`);
      }
    } catch {
      setMarking(false);
    }
  }

  if (error === 'access_denied') {
    return <Box sx={{ p: 3 }}><Alert severity="warning">{t('courses.noAccess')}</Alert></Box>;
  }
  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{t('courses.loadFailed')}</Alert></Box>;
  }
  if (!lesson) {
    return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  const prev = prevLesson();
  const next = nextLesson();

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{lesson.title}</Typography>
      <Divider sx={{ mb: 2 }} />

      {(lesson.contentItems || []).map((item, i) => (
        <ContentItem key={i} item={item} lessonId={lessonId} />
      ))}

      <Divider sx={{ mt: 3, mb: 2 }} />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={!prev}
          onClick={() => navigate(`/courses/${courseId}/lessons/${prev}`)}
        >
          {t('courses.prev')}
        </Button>

        <Button
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          onClick={handleMarkDone}
          disabled={marking}
        >
          {t('courses.markDone')}
        </Button>

        <Button
          endIcon={<ArrowForwardIcon />}
          disabled={!next}
          onClick={() => navigate(`/courses/${courseId}/lessons/${next}`)}
        >
          {t('courses.next')}
        </Button>
      </Stack>
    </Box>
  );
}
